import { useEffect } from 'react';
import { useClock } from './ClockProvider.js';
import styles from './AlarmTakeover.module.css';

/**
 * Full-page takeover modal that pops when a countdown hits zero or an
 * alarm fires. Big name + giant Stop button — designed to be obvious
 * from across the classroom so the teacher can dismiss without
 * hunting for a control. Esc / click-anywhere-but-the-button does NOT
 * dismiss; the teacher has to tap Stop on purpose.
 */
export function AlarmTakeover() {
  const { firing, dismissFiring } = useClock();

  // Allow Enter or Space when the Stop button isn't focused yet.
  useEffect(() => {
    if (!firing) return undefined;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        dismissFiring();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [firing, dismissFiring]);

  if (!firing) return null;

  const eyebrow = firing.source === 'countdown' ? "Time's up!" : 'Alarm';

  return (
    <div
      className={styles.takeover}
      role="alertdialog"
      aria-modal="true"
      data-testid="alarm-takeover"
    >
      <div className={styles.card}>
        <p className={styles.eyebrow}>{eyebrow}</p>
        <p className={styles.glyph} aria-hidden="true">
          ⏰
        </p>
        <h2 className={styles.name}>{firing.name}</h2>
        <button
          type="button"
          className={styles.stopButton}
          onClick={dismissFiring}
          autoFocus
          data-testid="alarm-dismiss"
        >
          Stop
        </button>
      </div>
    </div>
  );
}
